import { isAnalysisReviewItem, type StudyDocument, type StudyReviewItem } from './studyApi';
import type { StudyPracticeYearLevel } from './studyApi';
import { analysisPath } from './studyAnalysisApi';
import type { StudyAnalysisReviewItem, StudyPracticeSet } from './studyAnalysisFollowUpApi';
import { analysisReviewSourceCopy, analysisReviewTitle } from './studyAnalysisReviewCopy';
import { analysisReviewPath } from './studySessionQuestion';

/**
 * Pure rules of the Subject Space Practice tab (study-practice-tab-prd.md §2, §3). The tab reads
 * existing data only: the subject's materials, their analyses and practice sets, the Review
 * queue and the question bank. Nothing here charges Stars or calls AI.
 */

/** Rows in "Practise from my materials"; sets are read for these rows only (PRD §3). */
export const PRACTICE_MATERIAL_LIMIT = 10;

/** Analysis page parameters: open one practice set, and hide its earlier answers for a redo. */
export const PRACTICE_SET_PARAM = 'practiceSet';
export const PRACTICE_REDO_PARAM = 'redo';

/** Year levels the question bank serves (the backend accepts Year 7 to Year 12 only). */
const BANK_YEAR_LEVELS: readonly StudyPracticeYearLevel[] = [
  'Year 7',
  'Year 8',
  'Year 9',
  'Year 10',
  'Year 11',
  'Year 12',
];

/** Review sources that mean "this answer was wrong" (G-01 answer checks, G-05 marked tests). */
const MISTAKE_SOURCES: ReadonlySet<StudyAnalysisReviewItem['source']> = new Set([
  'answer_check',
  'marked_wrong',
  'practice_wrong',
]);

/** Newest first, at most 10. */
export function practiceMaterials(materials: readonly StudyDocument[]): StudyDocument[] {
  return [...materials]
    .sort((a, b) => b.created_at.localeCompare(a.created_at))
    .slice(0, PRACTICE_MATERIAL_LIMIT);
}

/** An analysis with a result: practice sets can be made from it. */
export function materialAnalysed(document: Pick<StudyDocument, 'analysis'>): boolean {
  const status = document.analysis?.status;
  return status === 'succeeded' || status === 'partial';
}

/** Photos and PDFs can be analysed; other files cannot. */
export function materialAnalysable(document: Pick<StudyDocument, 'mime_type'>): boolean {
  return document.mime_type === 'application/pdf' || document.mime_type.startsWith('image/');
}

/** The analysis page with one practice set open. `redo` hides the earlier answers. */
export function practiceSetPath(
  documentId: string,
  analysisId: string,
  setId: string,
  redo = false,
): string {
  const set = `&${PRACTICE_SET_PARAM}=${encodeURIComponent(setId)}`;
  return `${analysisPath(documentId, analysisId)}${set}${redo ? `&${PRACTICE_REDO_PARAM}=1` : ''}`;
}

/** Questions whose newest check said `correct`, of all questions in the set. */
export function practiceSetScore(set: Pick<StudyPracticeSet, 'questions'>) {
  const correct = set.questions.filter(
    (question) => question.latest_attempt?.verdict === 'correct',
  ).length;
  const tried = set.questions.filter((question) => question.attempts > 0).length;
  return { correct, tried, total: set.questions.length };
}

export function practiceSetScoreLabel(set: Pick<StudyPracticeSet, 'questions'>): string {
  const score = practiceSetScore(set);
  return score.tried === 0 ? 'Not started' : `${score.correct} of ${score.total} correct`;
}

/** The Study profile Year as a question-bank Year, or null when the bank has no such Year. */
export function bankYearLevel(yearLevel: number | null | undefined): StudyPracticeYearLevel | null {
  if (typeof yearLevel !== 'number') return null;
  return BANK_YEAR_LEVELS.find((year) => year === `Year ${yearLevel}`) ?? null;
}

/** A practice set and the material row it was read for. */
export interface PracticeSetEntry {
  set: StudyPracticeSet;
  documentId: string;
  /** The newest analysis of the material; the set opens on it. */
  analysisId: string;
  materialName: string;
}

/** One row in "My mistakes". */
export interface PracticeMistake {
  key: string;
  title: string;
  detail: string;
  path: string;
  due: boolean;
  /** The Review date, or the date of the wrong practice answer. */
  date: string;
}

/**
 * Wrong items of this subject: Review items from answer checks and marked tests (filtered by
 * subject, or by a material of this subject when the item has no subject). The server owns
 * practice mistakes and completion too; never resurrect a completed item from old attempts.
 */
export function subjectMistakes(input: {
  review: readonly StudyReviewItem[];
  subjectKey: string;
  documentIds: ReadonlySet<string>;
}): PracticeMistake[] {
  const fromReview = input.review
    .filter(isAnalysisReviewItem)
    .filter(
      (item) =>
        MISTAKE_SOURCES.has(item.source) &&
        (item.subject?.subject_key
          ? item.subject.subject_key === input.subjectKey
          : input.documentIds.has(item.document_id)),
    )
    .map(
      (item): PracticeMistake => ({
        key: `review:${item.id}`,
        title: analysisReviewTitle(item),
        detail: analysisReviewSourceCopy(item),
        path: analysisReviewPath(item),
        due: item.due,
        date: item.review_due_at,
      }),
    );

  return fromReview.sort((a, b) => Number(b.due) - Number(a.due) || a.date.localeCompare(b.date));
}
